import React from 'react';
import {
  Typography,
  Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Link, Button, TableSortLabel, Chip
} from '@mui/material';
import { AddOutlined, DeleteForever, MoreHorizOutlined, ThumbDownTwoTone, ThumbUpTwoTone } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { apiUrl } from '../../config';
import { EvaluationsApiResponse } from '../../interfaces/EvaluationApiResponse';
import { formatDate } from '../../utils/dateUtils';

interface TireRatingTableProps {
  evaluations: EvaluationsApiResponse[];
  searchInput?: string;
  onDeleteEvaluation: (id: number) => void;
}

const TireRatingTable: React.FC<TireRatingTableProps> = ({ evaluations, searchInput = '', onDeleteEvaluation}) => {
  const theme = useTheme();
  const [sortField, setSortField] = React.useState<string>('performedDate');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');
  
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const sortedEvaluations = React.useMemo(() => {
    return [...evaluations].sort((a, b) => {
      if (sortField === 'id') {
        // Handle null values safely
        if (a.id === null && b.id === null) return 0;
        if (a.id === null) return sortDirection === 'asc' ? -1 : 1;
        if (b.id === null) return sortDirection === 'asc' ? 1 : -1;
        // If neither is null, proceed with normal comparison
        return sortDirection === 'asc' ? a.id - b.id : b.id - a.id;
      } 
      if (sortField === 'owner') {
        const aOwner = a.vehicle.customer.company || a.vehicle.customer.name || '';
        const bOwner = b.vehicle.customer.company || b.vehicle.customer.name || '';
        return sortDirection === 'asc' 
          ? aOwner.localeCompare(bOwner)
          : bOwner.localeCompare(aOwner);
      }
      if (sortField === 'performedDate') {
        const aDate = new Date(a.performedDate);
        const bDate = new Date(b.performedDate);
        return sortDirection === 'asc' 
          ? aDate.getTime() - bDate.getTime() 
          : bDate.getTime() - aDate.getTime();
      }
      if (sortField === 'season') {
        return sortDirection === 'asc'
          ? a.season.localeCompare(b.season)
          : b.season.localeCompare(a.season);
      }
      return 0;
    });
  }, [evaluations, sortField, sortDirection]);

  const handleDeleteEvaluation = (id: number) => {
    if (window.confirm('Vil du slette denne vurderingen?')) {
      axios.delete(`${apiUrl}/admin/evaluations/delete/${id}`)
      .then(() => {
        onDeleteEvaluation(id);
      })
      .catch(error => {
        let errorMessage = 'Sletting feilet (' + apiUrl + ')\n"';

        if (error.response) {
          errorMessage += `Error: ${error.response.status}. ${error.response.data.message}`;
        } else if (error.request) {
          errorMessage += 'Fikk ikke svar fra serveren.';
        } else {
          errorMessage += error.message;
        }
      });
    } else {
      // User clicked Cancel
      // Do nothing
    }    
  };
  
  return (
    <Grid item xs={12} padding={1}>
      <Typography variant='body1' color="text.secondary">
        {`Antall vurderinger: ${evaluations.length}`}
      </Typography>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 1000}} component={Paper}>
        <Table stickyHeader aria-label="tabell dekkvurderinger">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'id'}
                  direction={sortField === 'id' ? sortDirection : 'asc'}
                  onClick={() => handleSort('id')}
                >
                  #
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'owner'}
                  direction={sortField === 'owner' ? sortDirection : 'asc'}
                  onClick={() => handleSort('owner')}
                >
                  Eier
                </TableSortLabel>
              </TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Epost</TableCell>
              <TableCell>Regnummer</TableCell>
              <TableCell>Plassering</TableCell>
              <TableCell>Status</TableCell>
              {searchInput.length >= 3 && 
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'season'}
                    direction={sortField === 'season' ? sortDirection : 'asc'}
                    onClick={() => handleSort('season')}
                  >
                    Sesong
                  </TableSortLabel>
                </TableCell>
              }
              {/* <TableCell>Registrert av</TableCell> */}
              <TableCell>
                <TableSortLabel
                  active={sortField === 'performedDate'}
                  direction={sortField === 'performedDate' ? sortDirection : 'asc'}
                  onClick={() => handleSort('performedDate')}
                >
                  Dato
                </TableSortLabel>
              </TableCell>
              <TableCell>Vurdering</TableCell>
              <TableCell>Detaljer</TableCell>
              <TableCell>Registrer</TableCell>
              <TableCell>Slett</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedEvaluations.map((evaluation) => (
              <TableRow key={evaluation.id}>
                <TableCell>{evaluation.id}</TableCell>
                <TableCell>
                  {evaluation.vehicle.customer.company
                    ? `${evaluation.vehicle.customer.company}${evaluation.vehicle.customer.name ? ` (${evaluation.vehicle.customer.name})` : ''}`
                    : evaluation.vehicle.customer.name}
                </TableCell>
                <TableCell>
                  <Link href={`tel:${evaluation.vehicle.customer.phone}`}>
                    {evaluation.vehicle.customer.phone}
                  </Link>
                </TableCell>
                <TableCell>
                  <Link href={`mailto:${evaluation.vehicle.customer.email}`}>
                    {evaluation.vehicle.customer.email}
                  </Link>
                </TableCell>
                <TableCell>{evaluation.vehicle.registrationNumber}</TableCell>
                <TableCell>{evaluation.vehicle.location}</TableCell>
                <TableCell>
                  {!evaluation.vehicle.hasTireHotel && (
                    <Chip color='warning' size='small' label='Flyttet ut' />
                  )}
                </TableCell>
                {searchInput.length >= 3 && <TableCell>{evaluation.season}</TableCell>}
                {/* <TableCell>{evaluation.performedBy}</TableCell> */}
                <TableCell>{formatDate(evaluation.performedDate)}</TableCell>
                <TableCell align='center'>
                  {evaluation.rating === 'bra' ? <ThumbUpTwoTone style={{ color: theme.palette.success.main }} /> :
                    evaluation.rating === 'middels' ? <ThumbUpTwoTone style={{ color: theme.palette.warning.main }} /> :
                      <ThumbDownTwoTone style={{ color: theme.palette.error.main }} />
                  }
                </TableCell>
                <TableCell align='center'>
                  <Link href={`/admin/auth/tirerating/${evaluation.id}/`}>
                    <MoreHorizOutlined />
                  </Link>
                </TableCell>
                <TableCell align='center'>
                  <Link href={`/admin/auth/tirerating/add?copyFrom=${evaluation.id}`}>
                    <AddOutlined />
                  </Link>
                </TableCell>
                <TableCell align='justify'>
                  <Button
                    variant="text"
                    onClick={() => handleDeleteEvaluation(evaluation.id ?? 0)}
                  >
                    <DeleteForever color='error' />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Paper>
    </Grid>
  );
}

export default TireRatingTable;
